import React, { useRef, useState } from 'react'
import { Button, Checkbox, Form, Modal, Segment, Header } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { analyzeTemplateWithAI, InspectionService, AITemplateService } from 'utility/services'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts'
import { GroupListModal } from '../inspection/Modal'
import { ShareNetwork, X } from 'phosphor-react'
import { useNotification } from 'hooks'
import { ReportText } from 'utility'

const languageOptions = [
    { key: 'en', text: 'English', value: 'English' },
    { key: 'hi', text: 'Hindi', value: 'Hindi' },
    { key: 'es', text: 'Spanish', value: 'Spanish' },
    { key: 'fr', text: 'French', value: 'French' },
    { key: 'pt', text: 'Portuguese', value: 'Portuguese' },
    { key: 'pt-br', text: 'Portuguese (Brazilian)', value: 'Portuguese (Brazilian)' },
    { key: 'zh-cn', text: 'Mandarin', value: 'Mandarin' },
    { key: 'ar', text: 'Arabic', value: 'Arabic' },
    { key: 'bn', text: 'Bengali', value: 'Bengali' },
    { key: 'ru', text: 'Russian', value: 'Russian' },
    { key: 'id', text: 'Indonesian', value: 'Indonesian' },
    { key: 'ur', text: 'Urdu', value: 'Urdu' },
    { key: 'de', text: 'German', value: 'German' },
    { key: 'ja', text: 'Japanese', value: 'Japanese' },
    { key: 'pcm', text: 'Nigerian Pidgin', value: 'Nigerian Pidgin' },
    { key: 'ar-eg', text: 'Egyptian Arabic', value: 'Egyptian Arabic' },
    { key: 'tr', text: 'Turkish', value: 'Turkish' },
    { key: 'zh-yue', text: 'Cantonese', value: 'Cantonese' },
    { key: 'tl', text: 'Tagalog', value: 'Tagalog' },
    { key: 'vi', text: 'Vietnamese', value: 'Vietnamese' },
    { key: 'ko', text: 'Korean', value: 'Korean' },
    { key: 'fa', text: 'Farsi (Persian)', value: 'Farsi (Persian)' },
]

const AnalyzeWithAIModal = ({ open, onClose, item }) => {
    // Add PDF-friendly styles
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            .ai-report-content * {
                font-family: Arial, sans-serif !important;
                color: #000000 !important;
                -webkit-font-smoothing: antialiased !important;
                -moz-osx-font-smoothing: grayscale !important;
                text-rendering: optimizeLegibility !important;
            }
            .ai-report-content h1,
            .ai-report-content h2,
            .ai-report-content h3,
            .ai-report-content h4,
            .ai-report-content h5 {
                font-weight: bold !important;
                margin: 8px 0 !important;
            }
            .ai-report-content p,
            .ai-report-content div {
                margin: 4px 0 !important;
                line-height: 1.4 !important;
            }
            @media print {
                .ai-report-content * {
                    font-family: Arial, sans-serif !important;
                    color: #000000 !important;
                }
            }
            @media (max-width: 768px) {
                .hide-on-mobile {
                    display: none !important;
                }
                .show-on-mobile {
                    display: inline !important;
                }
            }
            @media (min-width: 769px) {
                .hide-on-mobile {
                    display: inline !important;
                }
                .show-on-mobile {
                    display: none !important;
                }
            }
            @media (max-width: 600px) {
                .ui.modal > .actions {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 8px !important;
                }
                .ui.modal > .actions > .ui.button {
                    width: 100% !important;
                    margin: 0 !important;
                }
                .ui.modal > .actions > div {
                    width: 100% !important;
                    display: flex !important;
                    flex-direction: column !important;
                    gap: 8px !important;
                }
            }
        `
        document.head.appendChild(style)
        return () => document.head.removeChild(style)
    }, [])

    const [allQuestions, setAllQuestions] = useState(true)
    const [wantGraph, setWantGraph] = useState(false)
    const [useScoreSystem, setUseScoreSystem] = useState(false)
    const [groupBy, setGroupBy] = useState(null)
    const [language, setLanguage] = useState('en-US')
    const [suggestNextToQuestion, setSuggestNextToQuestion] = useState(true)
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)
    const [openReport, setOpenReport] = useState(false)
    const reportRef = useRef(null)
    const [pdfLoading, setPdfLoading] = useState(false)
    const [errorOpen, setErrorOpen] = useState(false)
    const [lastOptions, setLastOptions] = useState(null)
    const [openShareModal, setOpenShareModal] = useState(false)
    const { successToast } = useNotification()
    const [availableLanguages, setAvailableLanguages] = useState([])
    const [languagesLoading, setLanguagesLoading] = useState(false)

    // Fetch available languages from API
    React.useEffect(() => {
        const fetchLanguages = async () => {
            if (open && availableLanguages.length === 0) {
                setLanguagesLoading(true)
                try {
                    const response = await AITemplateService.getLanguages('A') // CallFrom: A for AI report analyze
                    console.log('Language API Response:', response) // Debug log
                    if (response) {
                        let languageOptions = []
                        if (Array.isArray(response)) {
                            languageOptions = response.map(lang => ({
                                key: lang.code || lang.Code,
                                text: lang.name || lang.Name,
                                value: lang.code || lang.Code,
                            }))
                        } else if (typeof response === 'object') {
                            languageOptions = Object.entries(response).map(
                                ([code, label]) => ({ key: code, text: String(label), value: code }),
                            )
                        }
                        console.log('Formatted Language Options:', languageOptions) // Debug log
                        setAvailableLanguages(languageOptions)
                    }
                } catch (error) {
                    console.error('Failed to fetch languages:', error)
                    // Fallback to hardcoded languages if API fails
                    setAvailableLanguages(languageOptions)
                } finally {
                    setLanguagesLoading(false)
                }
            }
        }
        fetchLanguages()
    }, [open, availableLanguages.length])

    // Set default language when API languages are loaded
    React.useEffect(() => {
        if (availableLanguages.length > 0 && language === 'en-US') {
            // Find English language from API or use first available
            const englishLang = availableLanguages.find(lang =>
                lang.key === 'en-US' ||
                lang.key === 'en' ||
                lang.text?.toLowerCase().includes('english'),
            )
            if (englishLang) {
                setLanguage(englishLang.value)
            } else if (availableLanguages[0]) {
                setLanguage(availableLanguages[0].value)
            }
        }
    }, [availableLanguages, language])

    const onSubmit = async () => {
        try {
            setLoading(true)
            setResult(null)

            const options = {
                allQuestions,
                wantGraph,
                useScoreSystem,
                language,
                suggestNextToQuestion,
                groupBy,
            }

            // Debug: Log the options being sent
            console.log('AI Report Options:', options)

            setLastOptions(options)

            const data = await analyzeTemplateWithAI(options, item)
            const rep = data?.report || null
            const isNullish = rep && rep.overall == null && rep.questions == null && rep.graphSuggestions == null
            if (!rep || isNullish) {
                setErrorOpen(true)
            } else {
                setResult(rep)
                setOpenReport(true)
            }
        } catch (err) {
            setErrorOpen(true)
        } finally {
            setLoading(false)
        }
    }

    const closeAndReset = () => {
        setResult(null)
        setLoading(false)
        setOpenReport(false)
        setErrorOpen(false)
        setLastOptions(null)
        setOpenShareModal(false)
        onClose?.()
    }

    const onCloseShareGroup = async (list) => {
        try {
            if (list.length && item?.Id) {
                await InspectionService.shareReport({ ReportId: item.Id, GroupId: list.toString() })
                successToast(ReportText.sharedToGroup)
            }
        } finally {
            setOpenShareModal(false)
        }
    }

    return (
        <>
        <Modal open={open} onClose={closeAndReset} size="large">
            <Modal.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Analyze with AI</span>
                <X
                    size={24}
                    style={{ cursor: 'pointer' }}
                    onClick={closeAndReset}
                />
            </Modal.Header>
            <Modal.Content scrolling>
                <Form>
                    <Form.Field>
                        <Checkbox
                            label="Analyze all questions"
                            checked={allQuestions}
                            onChange={(_, d) =>
                                setAllQuestions(Boolean(d.checked))
                            }
                        />
                    </Form.Field>
                    <Form.Field>
                        <Checkbox
                            label="Use score system"
                            checked={useScoreSystem}
                            onChange={(_, d) => setUseScoreSystem(Boolean(d.checked))}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Checkbox
                            label="Include AI Graph"
                            checked={wantGraph}
                            onChange={(_, d) => {
                                const checked = Boolean(d.checked)
                                setWantGraph(checked)
                                if (checked) setGroupBy('Section')
                                else setGroupBy(null)
                            }}
                        />
                    </Form.Field>
                    {/* Hide group-by controls per client; still send groupBy='Section' in payload */}
                    {/* Hide Use score system per client request; keep default false */}
                    <Form.Field>
                        <label>Suggestion language</label>
                        <Dropdown
                            selection
                            loading={languagesLoading}
                            options={availableLanguages.length > 0 ? availableLanguages : languageOptions}
                            value={language}
                            onChange={(_, d) => setLanguage(String(d.value))}
                            placeholder={languagesLoading ? 'Loading languages...' : 'Select language'}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Checkbox
                            label="Show suggestion next to each question"
                            checked={suggestNextToQuestion}
                            onChange={(_, d) =>
                                setSuggestNextToQuestion(Boolean(d.checked))
                            }
                        />
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={closeAndReset} disabled={loading}>
                    Close
                </Button>
                <Button
                    primary
                    onClick={onSubmit}
                    loading={loading}
                    disabled={loading}
                >
                    Generate
                </Button>
            </Modal.Actions>
        </Modal>
        <Modal open={errorOpen} onClose={() => setErrorOpen(false)} size="small">
            <Modal.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Something went wrong</span>
                <X
                    size={24}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setErrorOpen(false)}
                />
            </Modal.Header>
            <Modal.Content>
                <p>Something went wrong, please try again.</p>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={() => setErrorOpen(false)} disabled={loading}>Close</Button>
                <Button
                    primary
                    loading={loading}
                    disabled={loading}
                    onClick={async () => {
                        try {
                            setLoading(true)
                            const options = lastOptions || {
                                allQuestions,
                                wantGraph,
                                useScoreSystem,
                                language,
                                suggestNextToQuestion,
                                groupBy,
                            }
                            console.log('AI Report Options (Retry):', options)
                            const data = await analyzeTemplateWithAI(options, item)
                            const rep = data?.report || null
                            const isNullish = rep && rep.overall == null && rep.questions == null && rep.graphSuggestions == null
                            if (!rep || isNullish) {
                                // keep error modal open
                                return
                            }
                            setResult(rep)
                            setErrorOpen(false)
                            setOpenReport(true)
                        } catch (e) {
                            // stay on error modal
                        } finally {
                            setLoading(false)
                        }
                    }}
                >
                    Try Again
                </Button>
            </Modal.Actions>
        </Modal>
        <Modal open={openReport} onClose={() => setOpenReport(false)} size="large">
            <Modal.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{(item?.AuditName || 'Template')} - AI Report</span>
                <X
                    size={24}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setOpenReport(false)}
                />
            </Modal.Header>
            <Modal.Content scrolling>
                {result && (
                    <div
                        className="mt-1 ai-report-content"
                        ref={reportRef}
                        style={{
                            fontFamily: 'Arial, sans-serif',
                            fontSize: '14px',
                            lineHeight: '1.4',
                            color: '#000000',
                            backgroundColor: '#ffffff',
                        }}
                    >
                        {(() => {
                            let dataObj =
                                typeof result === 'string'
                                    ? (() => {
                                          try {
                                              return JSON.parse(result)
                                          } catch {
                                              return null
                                          }
                                      })()
                                    : result
                            // If model embedded JSON inside overall.summary, try to extract client-side
                            if (dataObj && (!Array.isArray(dataObj?.questions) || !dataObj.questions.length)) {
                                const inner = dataObj?.overall?.summary
                                const tryExtract = text => {
                                    if (!text || typeof text !== 'string') return null
                                    const repairToBalancedJson = str => {
                                        const stack = []
                                        for (let i = 0; i < str.length; i += 1) {
                                            const ch = str[i]
                                            if (ch === '{' || ch === '[') stack.push(ch)
                                            else if (ch === '}' || ch === ']') {
                                                const last = stack[stack.length - 1]
                                                if ((last === '{' && ch === '}') || (last === '[' && ch === ']')) {
                                                    stack.pop()
                                                }
                                            }
                                        }
                                        let suffix = ''
                                        while (stack.length) {
                                            const open = stack.pop()
                                            suffix += open === '{' ? '}' : ']'
                                        }
                                        return str + suffix
                                    }
                                    let parsed = null
                                    try { parsed = JSON.parse(text) } catch (e) { parsed = null }
                                    if (parsed) return parsed
                                    const fence = text.match(/```(?:json)?\n([\s\S]*?)\n```/)
                                    if (fence && fence[1]) {
                                        try { parsed = JSON.parse(fence[1]) } catch (e) { parsed = null }
                                        if (parsed) return parsed
                                        const repairedFenced = repairToBalancedJson(fence[1])
                                        try { parsed = JSON.parse(repairedFenced) } catch (e) { parsed = null }
                                        if (parsed) return parsed
                                    }
                                    const start = text.indexOf('{')
                                    const end = text.lastIndexOf('}')
                                    if (start !== -1 && end !== -1 && end > start) {
                                        const candidate = text.slice(start, end + 1)
                                        try { parsed = JSON.parse(candidate) } catch (e) { parsed = null }
                                        if (parsed) return parsed
                                        const repaired = repairToBalancedJson(candidate)
                                        try { parsed = JSON.parse(repaired) } catch (e) { parsed = null }
                                        if (parsed) return parsed
                                    }
                                    const repairedFull = repairToBalancedJson(text)
                                    try { parsed = JSON.parse(repairedFull) } catch (e) { parsed = null }
                                    if (parsed) return parsed
                                    return null
                                }
                                const extracted = tryExtract(inner)
                                if (extracted && Array.isArray(extracted?.questions)) {
                                    dataObj = extracted
                                }
                            }
                            if (!dataObj) {
                                return (
                                    <Segment>
                                        Could not parse AI response. Please try again.
                                    </Segment>
                                )
                            }
                            // Build question list: always rely solely on AI to avoid duplicates/hardcoded text
                            const aiQuestions = Array.isArray(dataObj?.questions) ? dataObj.questions : []
                            const questions = aiQuestions
                            // Heuristic derivation when AI didn't set correctness (for better graph and labels)
                            const deriveHeuristic = (q) => {
                                if (!q) return q
                                const text = String(q.question || '')
                                const expectsYes = /(clean|sealed|covered|stored|removed|hygiene|pest|debris|sanitize|sanitized|waste|garbage|temperature|label|secured|tight containers)/i.test(text)
                                const optionTexts = Array.isArray(q.options) ? q.options.map(o => String(o.text || '').toLowerCase()) : []
                                const hasYes = optionTexts.includes('yes')

                                // If AI didn't decide, but options include Yes/No, derive using compliance best-practice
                                if (q.isCorrect === null && (hasYes || expectsYes)) {
                                    const ua = q.userAnswer == null ? null : String(q.userAnswer).trim().toLowerCase()
                                    const correctAnswer = 'Yes'
                                    const isCorrect = ua === 'yes'
                                    const computed = {
                                        ...q,
                                        isCorrect: ua == null ? false : isCorrect,
                                        correctAnswer,
                                    }
                                    // Keep AI-provided suggestion if present; do not hardcode fallback text here
                                    return computed
                                }
                                return q
                            }
                            const derivedQuestions = questions.map(deriveHeuristic)

                            const correct = derivedQuestions.filter(q => q.isCorrect === true).length
                            const derivedScore = derivedQuestions.length ? Math.round((correct / derivedQuestions.length) * 100) : dataObj?.overall?.scorePercent
                            // Build graph from USER ANSWERS rather than correctness
                            const normalize = v => String(v || '').trim().toLowerCase()
                            const yesCount = derivedQuestions.filter(q => normalize(q.userAnswer) === 'yes').length
                            const noCount = derivedQuestions.filter(q => normalize(q.userAnswer) === 'no').length
                            const otherCount = derivedQuestions.filter(q => {
                                const ua = normalize(q.userAnswer)
                                return ua !== '' && ua !== 'yes' && ua !== 'no'
                            }).length
                            const blankCount = derivedQuestions.length - yesCount - noCount - otherCount
                            const overallBase = (dataObj && dataObj.overall && typeof dataObj.overall === 'object') ? dataObj.overall : {}
                            const overall = { ...overallBase, scorePercent: (overallBase && typeof overallBase.scorePercent === 'number') ? overallBase.scorePercent : derivedScore }
                            const chartData = [
                                { name: 'Yes', value: yesCount },
                                { name: 'No', value: noCount },
                                { name: 'Other', value: otherCount },
                                { name: 'Blank', value: blankCount },
                            ]
                            // Removed hardcoded suggestion helpers for non-English duplication; rely on API text only

                            return (
                                <>
                                    {/* Summary first */}
                                    <Header as="h4">Summary</Header>
                                    <Segment>
                                        <div style={{ marginBottom: 8 }}>
                                            <strong>Score:</strong> {overall?.scorePercent ?? '-'}%
                                        </div>
                                        <div style={{ whiteSpace: 'pre-wrap' }}>{overall?.summary}</div>
                                        {!!Array.isArray(dataObj?.overall?.suggestions) && dataObj.overall.suggestions.length > 0 && (
                                            <div style={{ marginTop: 8 }}>
                                                <strong>Insights:</strong>
                                                <ul>
                                                    {dataObj.overall.suggestions.map((s, i) => (
                                                        <li key={i}>{s}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </Segment>
                                    {/* Graph next - Prefer API-provided graph suggestions, fallback to user-answers summary */}
                                    {(() => {
                                        let apiCharts = []
                                        if (Array.isArray(dataObj?.graphSuggestions)) {
                                            apiCharts = dataObj.graphSuggestions
                                        } else if (Array.isArray(dataObj?.GraphSuggestionData)) {
                                            apiCharts = dataObj.GraphSuggestionData
                                        }
                                        if (apiCharts.length > 0) {
                                            const toLower = v => String(v || '').toLowerCase()
                                            const isYes = v => toLower(v) === 'yes'
                                            const buildDatasetFromSuggestion = (cfg) => {
                                                const xField = cfg?.xField || 'name'
                                                const yField = cfg?.yField || 'value'
                                                // Normalize chart type for any downstream use (currently inferred at render time)
                                                // const typeName = toLower(cfg?.chartType)
                                                const wantsSectionRate = xField === 'section' && /(rate|score|compliance|percent)/i.test(yField)
                                                if (wantsSectionRate) {
                                                    const bySection = {}
                                                    derivedQuestions.forEach(q => {
                                                        const key = q.section || 'Section'
                                                        if (!bySection[key]) bySection[key] = { total: 0, yes: 0, no: 0 }
                                                        bySection[key].total += 1
                                                        if (isYes(q.userAnswer)) bySection[key].yes += 1
                                                        else bySection[key].no += 1
                                                    })
                                                    const rows = Object.keys(bySection).map(k => ({
                                                        [xField]: k,
                                                        // Non-compliance rate based on user answers (No/Total)
                                                        [yField]: bySection[k].total ? Math.round((bySection[k].no / bySection[k].total) * 100) : 0,
                                                    }))
                                                    return rows
                                                }
                                                // Questions per section
                                                if (xField === 'section' && /count|question/i.test(yField)) {
                                                    const bySection = {}
                                                    derivedQuestions.forEach(q => {
                                                        const key = q.section || 'Section'
                                                        if (!bySection[key]) bySection[key] = 0
                                                        bySection[key] += 1
                                                    })
                                                    return Object.keys(bySection).map(k => ({ [xField]: k, [yField]: bySection[k] }))
                                                }
                                                const wantsAttendance = xField === 'date' && /attendance/i.test(yField)
                                                if (wantsAttendance) {
                                                    const attnQ = derivedQuestions.find(q => /attend|coming/i.test(String(q.question || '')))
                                                    const date = item?.CompletedOn || item?.TaskDate || new Date().toISOString()
                                                    let confirmed = 0
                                                    if (attnQ) {
                                                        confirmed = isYes(attnQ.userAnswer) ? 1 : 0
                                                    }
                                                    return [{ [xField]: date, [yField]: confirmed }]
                                                }
                                                // Fallback: per question yes(1)/no(0)
                                                const rows = derivedQuestions.map(q => {
                                                    const val = isYes(q.userAnswer) ? 1 : 0
                                                    const obj = {}
                                                    obj[xField] = q.question
                                                    obj[yField] = val
                                                    return obj
                                                })
                                                return rows
                                            }

                                            // Filter to section-based charts only (client requirement)
                                            const sectionCharts = apiCharts.filter(c => String(c?.xField || '').toLowerCase() === 'section')
                                            // Show only one chart (force bar). If none provided, synthesize one by section -> questionCount
                                            const preferred = sectionCharts.length > 0
                                                ? [sectionCharts[0]]
                                                : [{ title: 'Questions by Section', xField: 'section', yField: 'questionCount' }]

                                            const chartBlocks = preferred.map((cfg, idx) => {
                                                const title = cfg?.title || 'Chart'
                                                // Force bar chart regardless of response chartType (temporary)
                                                const chartType = 'bar'
                                                let raw = []
                                                if (Array.isArray(cfg?.data)) {
                                                    raw = cfg.data
                                                } else if (Array.isArray(cfg?.dataset)) {
                                                    raw = cfg.dataset
                                                } else if (Array.isArray(cfg?.series)) {
                                                    raw = cfg.series
                                                }
                                                const xKey = cfg?.xField || 'name'
                                                let yKey = cfg?.yField || 'value'
                                                if ((!Array.isArray(raw) || raw.length === 0)) {
                                                    raw = buildDatasetFromSuggestion(cfg)
                                                }
                                                if (Array.isArray(raw) && raw.length > 0 && !cfg?.yField) {
                                                    const firstKeys = Object.keys(raw[0] || {})
                                                    const inferred = firstKeys.find(k => k !== xKey)
                                                    if (inferred) yKey = inferred
                                                }
                                                if (!Array.isArray(raw) || raw.length === 0) {
                                                    return (
                                                        <Segment key={idx} className="mt-1">
                                                            <Header as="h5">{title}</Header>
                                                            {cfg?.rationale && <div style={{ color: '#555', marginBottom: 6 }}>{cfg.rationale}</div>}
                                                            <div style={{ color: '#999' }}>No chart data provided by API.</div>
                                                        </Segment>
                                                    )
                                                }
                                                const COLORS = ['#4183c4', '#2ca02c', '#ff7f0e', '#d62728', '#9467bd', '#8c564b']
                                                return (
                                                    <Segment key={idx} className="mt-1">
                                                        <Header as="h5">{title}</Header>
                                                        {cfg?.rationale && <div style={{ color: '#555', marginBottom: 6 }}>{cfg.rationale}</div>}
                                                        <div style={{ height: 340 }}>
                                                            <ResponsiveContainer width="100%" height="100%">
                                                                {(() => {
                                                                    if (chartType === 'line') {
                                                                        return (
                                                                            <LineChart data={raw}>
                                                                                <XAxis dataKey={xKey} interval={0} tick={{ fontSize: 12 }} angle={-15} height={50} label={{ value: xKey, position: 'insideBottom', offset: -5 }} />
                                                                                <YAxis allowDecimals={false} domain={[0, 'dataMax']} label={{ value: yKey, angle: -90, position: 'insideLeft' }} />
                                                                                <Tooltip formatter={(v) => (typeof v === 'number' ? v : String(v))} />
                                                                                <Line type="monotone" dataKey={yKey} stroke="#4183c4" strokeWidth={2} dot={false} />
                                                                            </LineChart>
                                                                        )
                                                                    }
                                                                    if (chartType === 'pie' || chartType === 'donut') {
                                                                        return (
                                                                            <PieChart>
                                                                                <Tooltip formatter={(v) => (typeof v === 'number' ? v : String(v))} />
                                                                                <Legend verticalAlign="bottom" align="center" wrapperStyle={{ paddingTop: 8 }} />
                                                                                <Pie data={raw} dataKey={yKey} nameKey={xKey} cx="50%" cy="45%" innerRadius={chartType === 'donut' ? 60 : 0} outerRadius={90} fill="#4183c4">
                                                                                    {raw.map((entry, i) => (
                                                                                        <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                                                                                    ))}
                                                                                </Pie>
                                                                            </PieChart>
                                                                        )
                                                                    }
                                                                    if (chartType === 'stackedbar') {
                                                                        return (
                                                                            <BarChart data={raw} margin={{ top: 8, right: 16, bottom: 32, left: 16 }}>
                                                                                <XAxis dataKey={xKey} interval={0} tick={{ fontSize: 12 }} angle={-15} height={50} label={{ value: xKey, position: 'insideBottom', offset: -5 }} />
                                                                                <YAxis allowDecimals={false} domain={[0, 'dataMax']} label={{ value: yKey, angle: -90, position: 'insideLeft' }} />
                                                                                <Tooltip formatter={(v) => (typeof v === 'number' ? v : String(v))} />
                                                                                {Object.keys(raw[0] || {}).filter(k => k !== xKey).map((k, i) => (
                                                                                    <Bar key={k} dataKey={k} stackId="a" fill={COLORS[i % COLORS.length]} />
                                                                                ))}
                                                                            </BarChart>
                                                                        )
                                                                    }
                                                                    if (chartType === 'sparkline') {
                                                                        return (
                                                                            <LineChart data={raw}>
                                                                                <Tooltip formatter={(v) => (typeof v === 'number' ? v : String(v))} />
                                                                                <Line type="monotone" dataKey={yKey} stroke="#4183c4" strokeWidth={2} dot={false} />
                                                                            </LineChart>
                                                                        )
                                                                    }
                                                                    return (
                                                                        <BarChart data={raw} margin={{ top: 8, right: 16, bottom: 40, left: 16 }}>
                                                                            <XAxis dataKey={xKey} interval={0} tick={{ fontSize: 12 }} angle={-15} height={50} label={{ value: xKey, position: 'insideBottom', offset: -5 }} />
                                                                            <YAxis allowDecimals={false} domain={[0, 'dataMax']} label={{ value: yKey, angle: -90, position: 'insideLeft' }} />
                                                                            <Tooltip formatter={(v) => (typeof v === 'number' ? v : String(v))} />
                                                                            <Bar dataKey={yKey} fill="#4183c4" />
                                                                        </BarChart>
                                                                    )
                                                                })()}
                                                            </ResponsiveContainer>
                                                        </div>
                                                    </Segment>
                                                )
                                            })
                                            // Determine if at least one suggestion can render a chart (including auto-built datasets)
                                            const anyRenderable = preferred.some(cfg => {
                                                let raw = []
                                                if (Array.isArray(cfg?.data)) raw = cfg.data
                                                else if (Array.isArray(cfg?.dataset)) raw = cfg.dataset
                                                else if (Array.isArray(cfg?.series)) raw = cfg.series
                                                if (!Array.isArray(raw) || raw.length === 0) {
                                                    raw = buildDatasetFromSuggestion(cfg)
                                                }
                                                return Array.isArray(raw) && raw.length > 0
                                            })
                                            return (
                                                <div className="mt-2">
                                                    <Header as="h4">Graphs</Header>
                                                    {chartBlocks}
                                                    {/* If none of the charts can render, also show fallback */}
                                                    {!anyRenderable && (
                                                        <Segment className="mt-2">
                                                            <Header as="h5">User Answers Summary</Header>
                                                            <div style={{ height: 220 }}>
                                                                <ResponsiveContainer width="100%" height="100%">
                                                                    <BarChart data={chartData}>
                                                                        <XAxis dataKey="name" />
                                                                        <YAxis allowDecimals={false} />
                                                                        <Tooltip />
                                                                        <Bar dataKey="value" fill="#4183c4" />
                                                                    </BarChart>
                                                                </ResponsiveContainer>
                                                            </div>
                                                        </Segment>
                                                    )}
                                                </div>
                                            )
                                        }
                                        if (questions.length) {
                                            return (
                                                <Segment className="mt-2">
                                                    <Header as="h5">User Answers Summary</Header>
                                                    <div style={{ height: 220 }}>
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <BarChart data={chartData}>
                                                                <XAxis dataKey="name" />
                                                                <YAxis allowDecimals={false} />
                                                                <Tooltip />
                                                                <Bar dataKey="value" fill="#4183c4" />
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                    </div>
                                                </Segment>
                                            )
                                        }
                                        return null
                                    })()}
                                    {/* Questions next - block layout */}
                                    {!!questions.length && (
                                        <div className="mt-2">
                                            <Header as="h4">Per Question</Header>
                                            <div>
                                                {derivedQuestions.map((q, idx) => (
                                                    <Segment
                                                        key={idx}
                                                        color={(() => {
                                                            if (q.isCorrect === true) return 'green'
                                                            if (q.isCorrect === false) return 'red'
                                                            return undefined
                                                        })()}
                                                    >
                                                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{q.section}</div>
                                                        <div style={{ marginBottom: 4 }}>{q.question}</div>
                                                        <div style={{ marginBottom: 8, color: '#555' }}>
                                                            <strong>Suggestion:</strong> {q.suggestion || '-'}
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                                            <div><strong>User Answer:</strong> {q.userAnswer ?? '-'}</div>
                                                        <div>
                                                            <strong>Correct:</strong>{' '}
                                                            {(() => {
                                                                if (q.isCorrect === null) return 'Unknown'
                                                                const ca = q?.correctAnswer
                                                                if (ca != null && String(ca).trim() !== '') return String(ca)
                                                                if (q.isCorrect === true) return q.userAnswer ?? 'Yes'
                                                                return '-'
                                                            })()}
                                                        </div>
                                                        </div>
                                                    </Segment>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {/* Graph moved above */}
                                    {/* Removed global recommendations; suggestions are shown per question */}
                                </>
                            )
                        })()}
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
            }}>
                <Button onClick={() => setOpenReport(false)}>Close</Button>
                <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                }}>
                    <Button
                        onClick={() => setOpenShareModal(true)}
                        style={{
                            minWidth: 'auto',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <ShareNetwork size={16} />
                        <span className="hide-on-mobile">Share to Group</span>
                        <span className="show-on-mobile">Share</span>
                    </Button>
                    <Button
                        primary
                        loading={pdfLoading}
                        disabled={pdfLoading}
                        style={{
                            minWidth: 'auto',
                            whiteSpace: 'nowrap',
                        }}
                        onClick={async () => {
                        const loadScript = src => new Promise(resolve => {
                            const s = document.createElement('script')
                            s.src = src
                            s.onload = resolve
                            document.body.appendChild(s)
                        })
                        try {
                            setPdfLoading(true)
                            // Load html2pdf bundle (includes html2canvas + jsPDF) lazily
                            if (!window.html2pdf) {
                                await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js')
                            }
                            const node = reportRef.current
                            if (!node) return
                            const fileName = `${(item?.AuditName || 'AI Report').replace(/[^a-z0-9\- ]/gi,'_')}.pdf`
                            // Temporarily apply PDF-friendly styles
                            const originalStyles = {}
                            const styleElements = node.querySelectorAll('*')
                            styleElements.forEach(el => {
                                const computedStyle = window.getComputedStyle(el)
                                originalStyles[el] = {
                                    fontFamily: el.style.fontFamily,
                                    fontSize: el.style.fontSize,
                                    fontWeight: el.style.fontWeight,
                                    lineHeight: el.style.lineHeight,
                                    color: el.style.color,
                                    backgroundColor: el.style.backgroundColor,
                                }
                                // Apply PDF-friendly styles
                                if (computedStyle.fontFamily && !computedStyle.fontFamily.includes('Arial') && !computedStyle.fontFamily.includes('sans-serif')) {
                                    el.style.fontFamily = 'Arial, sans-serif'
                                }
                                if (!computedStyle.fontSize || computedStyle.fontSize === '0px') {
                                    el.style.fontSize = '12px'
                                }
                                el.style.color = '#000000'
                                el.style.backgroundColor = 'transparent'
                            })
                            const opt = {
                                margin: [15, 15, 15, 15],
                                filename: fileName,
                                image: { type: 'jpeg', quality: 0.98 },
                                html2canvas: {
                                    scale: 2,
                                    useCORS: true,
                                    scrollY: 0,
                                    backgroundColor: '#ffffff',
                                    letterRendering: true,
                                    logging: false,
                                },
                                jsPDF: {
                                    unit: 'mm',
                                    format: 'a4',
                                    orientation: 'portrait',
                                    compress: true,
                                },
                                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
                            }
                            await window.html2pdf()
                                .set(opt)
                                .from(node)
                                .toPdf()
                                .get('pdf')
                                .then(pdf => {
                                    pdf.save(fileName)
                                })
                            // Restore original styles
                            Object.keys(originalStyles).forEach(el => {
                                const styles = originalStyles[el]
                                el.style.fontFamily = styles.fontFamily
                                el.style.fontSize = styles.fontSize
                                el.style.fontWeight = styles.fontWeight
                                el.style.lineHeight = styles.lineHeight
                                el.style.color = styles.color
                                el.style.backgroundColor = styles.backgroundColor
                            })
                        } catch (e) {
                            console.error('PDF generation failed:', e)
                            // Could add toast notification here if available
                        } finally {
                            setPdfLoading(false)
                        }
                    }}
                >
                    Download PDF
                </Button>
                </div>
            </Modal.Actions>
        </Modal>
        <GroupListModal open={openShareModal} onClose={onCloseShareGroup} />
        </>
    )
}

export default AnalyzeWithAIModal
